// routes.ts — THE route → label table. One row per screen: the path, the
// nav's short word, the canonical screen name, the icon where the nav needs
// one, and which chrome shows it.
//
// Sweep item 71 (docs/SWEEP-SCOPE-2026-09-13.md §8): the rail, the phone
// tabs, the top bar, the ⌘K palette and Home's action row each kept their own
// list, and they drifted — the top bar read "Chain activity" over a screen
// titled Chain, the palette listed "Profit and loss" for Profit, the rail said
// Swap where the screen said Convert, and Home's four actions were not the
// rail's four. One table, read by all five: a screen renamed here is renamed
// everywhere at once (the operator's "fix once, applies everywhere").
//
// `label` is the nav's short word — §6 "Screen names" lets the nav shorten a
// name to its first word (Money, Settle, Sheet, Close). `title` is the
// screen's canonical name, the word its own header uses: the top bar and the
// palette print it, Home's action row prints it, and a short nav word carries
// it as the tooltip. Nothing else in the app spells a screen's name.

import type { LucideIcon } from 'lucide-react';
import { PHONE_TABS_SET, uiV2, type PhoneTabsSet } from './ui-v2';
import {
  LayoutDashboard, Wallet, Users, ArrowLeftRight, LineChart, Percent, ArrowRightLeft, ArrowDownUp,
  TrendingUp, Boxes, Landmark, CalendarCheck, Link2, Building2, Handshake, History, ShieldCheck,
  Repeat, Receipt, HandCoins, PiggyBank, Scale, FileText, ListChecks, UserPlus, Truck, Zap, WalletCards, UserCheck, Vault,
  Ellipsis, Plus,
} from 'lucide-react';

/** DAILY — the six screens the desk opens every day, always on show.
 *  MORE — the reports and windows, behind one fold on the rail and one "More"
 *  tab on the phone. ACTION — the doors pinned to the bottom of the rail, the
 *  top of the phone's More sheet and Home's action row. */
export type NavGroup = 'daily' | 'more' | 'action';

export interface RouteEntry {
  /** The path as the router matches it; a `:param` segment matches one path
   *  segment, and a query string is carried only for the palette's link. */
  path: string;
  /** The nav's short word. */
  label: string;
  /** The canonical screen name (§6 "Screen names"). */
  title: string;
  icon?: LucideIcon;
  nav?: NavGroup;
  /** Home only: the NavLink lights on the exact path, not on every path. */
  end?: boolean;
  /** Listed in the ⌘K palette. Off for a screen that needs a party or a deal
   *  in the URL — those are reached from the page that has one. */
  palette?: boolean;
  /** The gold button on Home's action row. */
  primary?: boolean;
  /** WHERE THE REBUILT CONSOLE LISTS IT (UI rebuild batch 1, lib/ui-v2.ts) — read only by the v2 shell, More and New
   *  entry, so main's nav above is untouched while the switch is off. */
  v2?: V2Place;
}

/** The rebuilt console's places for a screen (Astra's navigation, adjusted to the Books' rules):
 *  · `rail` — the laptop sidebar's two groups, Daily work and Control (More closes Control);
 *  · `section` — a tile on the More page (the phone's More tab, the sidebar's More), under that heading;
 *  · `entry` — a tile on New entry, under that heading;
 *  · `sub` — a tile's second line, ≤ 4 words;
 *  · `under` — the sidebar item that lights while this screen is open, when it is not on the sidebar itself;
 *  · `title` — the screen's name in batch 5 when it is not `title` (navTitle).
 *  Adjust balance has no place on either page: the audit door keeps its friction (palette, party page and Treasury;
 *  operator ruling C1, 26 Oct 2026). */
export interface V2Place {
  rail?: 'daily' | 'control' | 'reports';
  section?: MoreSection;
  entry?: EntrySection;
  sub?: string;
  under?: string;
  title?: string;
}
/** The More page's four sections, in page order. */
export type MoreSection = 'Daily work' | 'Control' | 'Reports' | 'Administration';
/** New entry's three groups, in page order: what the entry IS, then use (his words, 1 Nov 2026: "they also should be in logical
 *  ordering/group not just based on the most used"). A deal with a party; money a party sends, receives or is owed; the desk's own
 *  accounts and the owner's. The groups' own order is their use too: trades 257, party money 191, desk and owner 76 (30 days). */
export type EntrySection = 'Trades' | 'Party money' | 'Desk and owner';
export const MORE_SECTIONS: readonly MoreSection[] = ['Daily work', 'Control', 'Reports', 'Administration'];
export const ENTRY_SECTIONS: readonly EntrySection[] = ['Trades', 'Party money', 'Desk and owner'];

/** THE CUSTODY MODULE IS SWITCHED OFF on the Worker (CUSTODY_ENABLED) until the operator says (24 Oct 2026, "the wallet included, still switched
 *  off"): while it is, the console shows no Custody entry — not in the rail's fold, not in the phone's More sheet, not in the ⌘K palette — and
 *  its routes answer "not wired" to anyone who types the URL (the entry stays in the table, unlisted, so the top bar still names the screen).
 *  Turn it on WITH the Worker's switch, in the same release. */
// ON (his "custody not yet on the main? when we can bring it there", 2 Nov 2026; the Worker's switch is on since 30 Oct): the
// rebuilt console draws custody's four screens at /custody and lists Custody on its rail; the old console keeps today's v1
// section at /custody, unlisted, for the wallet's live test.
export const CUSTODY_IN_CONSOLE = true;

/** Table order is palette order: the doors first (what the desk does), then
 *  the daily screens, the reports, and the rare windows. Within a nav group
 *  the order here is the order on the rail. */
export const ROUTES: readonly RouteEntry[] = [
  // ── ACTION — the four doors, one ordered list for the rail, the phone
  //    sheet and Home. Convert joined them on 12 Sept 2026 (evening): the
  //    operator asked for one screen to convert any balance, party or desk
  //    account, from the left side. Adjust balance is the audit door and keeps
  //    its friction: palette, party page and Treasury, never a pinned button.
  { path: '/trade',              label: 'Book',    title: 'Book trade',   icon: ArrowLeftRight, nav: 'action', primary: true, v2: { entry: 'Trades', sub: 'Agree a deal', under: '/trades' } },
  { path: '/record',             label: 'Record',  title: 'Record money', icon: ArrowDownUp,    nav: 'action', v2: { entry: 'Party money', sub: 'Money in or out', under: '/movements' } },
  // IN A GROUP, ORDER BY USE (his "convert button I need handy … check with actual usage data", 1 Nov 2026; the Books' last 30
  // days: trades 157, money out 90, money in 82, Convert 71, Move 51, cheque 29, between parties 17, expense 15, drawing 10,
  // adjust 7). This table's order is each group's order.
  { path: '/swap',               label: 'Convert', title: 'Convert',      icon: Repeat,         nav: 'action', v2: { entry: 'Trades', sub: 'One asset to another', under: '/treasury' } },
  { path: '/treasury/rebalance', label: 'Move',    title: 'Move money',   icon: ArrowRightLeft, nav: 'action', v2: { entry: 'Desk and owner', sub: 'Between desk accounts', under: '/treasury' } },

  // Record money's other modes earn a palette line, not a button: weekly, not
  // daily (operator rule 2026-08-12). Between parties rides a query string.
  { path: '/mc',                 label: 'Cheque',          title: "Manager's cheque",  icon: FileText, v2: { entry: 'Trades', sub: 'Cheque to a seller', under: '/trades' } },
  { path: '/record?mode=p2p',    label: 'Between parties', title: 'Between parties',   icon: Users, v2: { entry: 'Party money', sub: 'Party to party', under: '/movements' } },
  { path: '/record/expense',     label: 'Expense',         title: 'Record expense',    icon: Receipt, v2: { entry: 'Desk and owner', sub: 'A desk cost', under: '/movements' } },
  { path: '/record/drawing',     label: 'Drawing',         title: 'Record drawing',    icon: HandCoins, v2: { entry: 'Desk and owner', sub: 'Owner takes money out', under: '/movements' } },
  { path: '/record?mode=profit_share', label: 'Profit share', title: 'Profit share',  icon: Users, v2: { entry: 'Party money', sub: 'A party\'s agreed share', under: '/movements' } },
  { path: '/record?mode=charge', label: 'Charge',          title: 'Record delivery charge', icon: Truck, v2: { entry: 'Party money', sub: 'A delivery charge', under: '/movements' } },
  { path: '/treasury/adjust',    label: 'Adjust',          title: 'Adjust balance',    icon: Scale, v2: { under: '/treasury' } },
  { path: '/record/capital',     label: 'Capital',         title: 'Record capital in', icon: PiggyBank, v2: { entry: 'Desk and owner', sub: 'Owner puts money in', under: '/movements' } },

  // ── DAILY — in the order a day runs: what was agreed (Trades), what moved
  //    (Money), what has not moved yet (Settle), where the money sits
  //    (Treasury), and who it sits with (Parties).
  { path: '/',          label: 'Home',     title: 'Home',            icon: LayoutDashboard, nav: 'daily', end: true, v2: { rail: 'daily' } },
  { path: '/trades',    label: 'Trades',   title: 'Trades',          icon: LineChart,       nav: 'daily', v2: { rail: 'daily', section: 'Daily work', sub: 'What was agreed' } },
  { path: '/movements', label: 'Money',    title: 'Money movements', icon: History,         nav: 'daily', v2: { rail: 'daily', section: 'Daily work', sub: 'What actually moved' } },
  { path: '/settle',    label: 'Settle',   title: 'Settle later',    icon: Handshake,       nav: 'daily', v2: { rail: 'daily', section: 'Daily work', sub: 'What has not moved' } },
  // "Wallets", not "Treasury" (N-091, his "ofc", 30 Oct 2026): Treasury names the 2-of-3 wallet alone — TERMINOLOGY §2.
  { path: '/treasury',  label: 'Wallets', title: 'Wallets',          icon: Wallet,          nav: 'daily', v2: { rail: 'daily', section: 'Daily work', sub: 'Where the money sits' } },
  { path: '/parties',   label: 'Parties',  title: 'Parties',         icon: Users,           nav: 'daily', v2: { rail: 'daily' } },
  // Daily since 15 Sept 2026 (operator: "this chain toggle I am using more — let it come out of the More").
  { path: '/chain',     label: 'Chain',    title: 'Chain',           icon: Link2,           nav: 'daily', v2: { rail: 'daily' } },

  // ── MORE — reports and read-only windows. Profit, its working (Inventory),
  //    the position at an instant (Sheet), cost-vs-price (Trading), the month
  //    end (Close), then the chain, the exchanges and the screener.
  { path: '/profit',        label: 'Profit',    title: 'Profit',        icon: TrendingUp,    nav: 'more', v2: { rail: 'reports', section: 'Reports', sub: 'Income, costs, result' } },
  { path: '/inventory',     label: 'Inventory', title: 'Inventory',     icon: Boxes,         nav: 'more', v2: { section: 'Reports', sub: 'Holdings and their cost' } },
  { path: '/balance-sheet', label: 'Sheet',     title: 'Balance sheet', icon: Landmark,      nav: 'more', v2: { section: 'Reports', sub: 'Position at a date' } },
  { path: '/trading',       label: 'Trading',   title: 'Trading',       icon: Percent,       nav: 'more', v2: { section: 'Reports', sub: 'Cost against price' } },
  { path: '/periods',       label: 'Close',     title: 'Period close',  icon: CalendarCheck, nav: 'more', v2: { rail: 'control', section: 'Control', sub: 'Check and close months' } },
  { path: '/exchanges',     label: 'Exchanges', title: 'Exchanges',     icon: Building2,     nav: 'more', v2: { rail: 'control', section: 'Control', sub: 'Venues against the books' } },
  { path: '/kyt',           label: 'KYT',       title: 'KYT',           icon: ShieldCheck,   nav: 'more', v2: { rail: 'control', section: 'Control', sub: 'Payments to screen' } },
  // Onboarding (6–7 Oct 2026): every lead that needs a person, each in full,
  // and every approved client, from the onboarding worker. Read-only —
  // decisions stay in the Telegram ops group.
  { path: '/onboarding',    label: 'Onboarding', title: 'Onboarding',   icon: UserCheck,     nav: 'more', v2: { section: 'Control', sub: 'Clients needing a person' } },
  // The energy desk (1 Oct 2026): TRON energy rented for the treasury wallets.
  // A window onto a separate service, like KYT — the Books run none of it.
  { path: '/energy',        label: 'Energy',    title: 'Energy',        icon: Zap,           nav: 'more', v2: { section: 'Control', sub: 'TRON energy for wallets' } },
  // The wallet register (6 Oct 2026): every address the books know, retired
  // and archived one way, never deleted (db/0064, routes/wallets.ts).
  // "Wallets" names ONE screen (P-076, 29 Oct 2026): custody's money screen. The register is the Wallet register everywhere.
  { path: '/wallets',       label: 'Wallet register', title: 'Wallet register', icon: WalletCards, nav: 'more', v2: { section: 'Administration', sub: 'Named addresses' } },
  // Custody (16 Oct 2026): the wallet system on main's model — deposits, the
  // floats, approvals for the Ledger, payout addresses. Behind the Worker's
  // CUSTODY_ENABLED switch; the screen says "not wired" until it is on.
  { path: '/custody',       label: 'Custody',   title: 'Custody',       icon: Vault,
    // Daily work by use: what waits on him to sign and the day's deposits. No `nav` — the old console's More stays as it is.
    ...(CUSTODY_IN_CONSOLE ? { v2: { rail: 'daily' as const, section: 'Daily work' as const, sub: 'Deposits and signing', title: 'Custody' } } : { palette: false, v2: { title: 'Custody' } }) },   // "Custody", never a second "Wallets" (the redesign, 2 Nov 2026)

  // ── Rare windows — the palette is their front door. Names left the nav on
  //    7 Sept 2026 (the legacy name-attachment tool, not daily); the legacy
  //    checklist's era ended 1 Sept.
  { path: '/migration',     label: 'Legacy',       title: 'Legacy history',        icon: ListChecks, v2: { section: 'Administration', sub: 'Closed historic records' } },
  { path: '/attach-names',  label: 'Names',        title: 'Names',                 icon: UserPlus, v2: { section: 'Administration', sub: 'Unnamed legacy parties' } },
  { path: '/inter-entity',  label: 'Inter-entity', title: 'Inter-entity transfer', icon: ArrowLeftRight },

  // ── The rebuilt console's two hubs (batch 1): More holds what the phone's four tabs do not; New entry holds every
  //    door that records something. Mounted only while batch 1 is switched on (App.tsx).
  { path: '/more',                     label: 'More',        title: 'More',             icon: Ellipsis, palette: false },
  { path: '/new',                      label: 'New',         title: 'New entry',        icon: Plus,       palette: false },

  // ── Named for the top bar only: reached from a page that has the party or
  //    the deal, never by name.
  { path: '/parties/new',              label: 'New party',   title: 'New party',        palette: false },
  { path: '/parties/:slug/convert',    label: 'Convert',     title: 'Convert balance',  palette: false },
  { path: '/parties/:slug/sub-broker', label: 'Sub-broker',  title: 'Sub-broker trade', palette: false, v2: { under: '/trades' } },
  // The chain-swap instrument was designed out; its route opens Move money.
  { path: '/treasury/chain-swap',      label: 'Move',        title: 'Move money',       palette: false },
];

/** The rows one chrome shows, in rail order. */
export function navRoutes(group: NavGroup): RouteEntry[] {
  return ROUTES.filter((r) => r.nav === group);
}

/** The rows the ⌘K palette lists, in table order. */
export function paletteRoutes(): RouteEntry[] {
  return ROUTES.filter((r) => r.palette !== false);
}

/** The path without its query string — the part the router matches. */
function barePath(path: string): string {
  return path.split('?')[0] ?? path;
}

/** Path → matcher. `:param` is one segment. */
function matcher(path: string): RegExp {
  const re = barePath(path).split('/').map((seg) => (seg.startsWith(':') ? '[^/]+' : seg.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))).join('/');
  return new RegExp(`^${re}(?:/|$)`);
}

/** Longest bare path first, so `/treasury/rebalance` wins over `/treasury`
 *  and `/parties/new` over the party page. Home (`/`) matches only itself.
 *
 *  A row with a query string (`/record?mode=p2p`) is a palette link, not a
 *  pathname: it never takes part in matching. Left in, its bare matcher
 *  `^/record` outranked `/record/expense` by raw string length and the top
 *  bar read "Between parties" over every Record door (found 14 Sept 2026).
 *  The sort keys on the bare path for the same reason. */
const MATCHERS: ReadonlyArray<readonly [RegExp, RouteEntry]> = [...ROUTES]
  .filter((r) => r.path !== '/' && !r.path.includes('?'))
  .sort((a, b) => barePath(b.path).length - barePath(a.path).length)
  .map((r) => [matcher(r.path), r] as const);

/** A route's name on the rail, More, the palette and the top bar: its batch-5 name while batch 5 is on — custody lands on the
 *  Wallets money screen (screens/v2/custody/Money.tsx) and is called what it opens — else its title. */
export function navTitle(r: RouteEntry): string {
  return uiV2(5) && r.v2?.title ? r.v2.title : r.title;
}

/** The canonical screen name for a pathname — the top bar's word. A path the
 *  table does not know reads as Home, never as a slug. */
export function screenTitle(pathname: string): string {
  for (const [re, r] of MATCHERS) if (re.test(pathname)) return navTitle(r);
  return 'Home';
}

/** Is this path one of the MORE screens? Lights the rail's fold when the
 *  operator is on a folded screen (arrived by ⌘K or by URL). */
export function inMoreGroup(pathname: string): boolean {
  return navRoutes('more').some((r) => pathname === r.path || pathname.startsWith(`${r.path}/`));
}

// ── THE REBUILT CONSOLE'S NAVIGATION (batch 1) ──────────────────────────────

/** One row by its exact path (a typo fails loudly). */
export function routeAt(path: string): RouteEntry {
  const r = ROUTES.find((x) => x.path === path);
  if (!r) throw new Error(`routes: no row for ${path}`);
  return r;
}

/** The rows the v2 sidebar shows in one of its groups, in table order. */
export function railRoutes(group: 'daily' | 'control' | 'reports'): RouteEntry[] {
  return ROUTES.filter((r) => r.v2?.rail === group);
}
/** The More page's tiles in one section, in table order. */
export function moreRoutes(section: MoreSection): RouteEntry[] {
  return ROUTES.filter((r) => r.v2?.section === section);
}
/** New entry's tiles in one group, in table order. */
export function entryRoutes(section: EntrySection): RouteEntry[] {
  return ROUTES.filter((r) => r.v2?.entry === section);
}

/** THE PHONE'S TAB BAR — the operator's question C4 (26 Oct 2026), so both answers are built and one switch picks
 *  (lib/ui-v2.ts PHONE_TABS_SET):
 *  · `astra` — Home, Parties, New entry, Chain, More: Trades, Money movements, Settle later and Treasury are tiles on
 *    More, the doors are on New entry (Astra's bottom nav; the lane's recommended default);
 *  · `daily` — main's seven daily screens and More, as the phone has them today; the doors lead More's page. */
export const PHONE_TAB_SETS = {
  astra: ['/', '/parties', '/new', '/chain', '/more'],
  daily: ['/', '/trades', '/movements', '/settle', '/treasury', '/parties', '/chain', '/more'],
} as const satisfies Record<PhoneTabsSet, readonly string[]>;
export const PHONE_TABS: readonly string[] = PHONE_TAB_SETS[PHONE_TABS_SET];

/** The table's row for a pathname: longest path first; Home only for itself; null for a path the table does not know. */
function rowFor(pathname: string): RouteEntry | null {
  if (pathname === '/') return ROUTES.find((r) => r.path === '/') ?? null;
  for (const [re, r] of MATCHERS) if (re.test(pathname)) return r;
  return null;
}

/** THE SIDEBAR ITEM THAT LIGHTS: the screen itself when it is on the sidebar, else the item it sits `under` (Record
 *  expense → Money movements, a party's page → Parties), else More — so the sidebar never shows nothing lit. New
 *  entry lights nothing: its button is in the top bar. */
export function sidebarOwner(pathname: string): string | null {
  const r = rowFor(pathname);
  if (!r || r.path === '/new') return null;
  if (r.v2?.rail) return r.path;
  // The four reports are ONE sidebar item, "Reports" (Profit's row; the report tabs are inside): any report lights it.
  if (r.v2?.section === 'Reports') return '/profit';
  if (r.v2?.under) return r.v2.under;
  if (pathname.startsWith('/parties')) return '/parties';
  return '/more';
}

/** THE PHONE TAB THAT LIGHTS: a tab for itself and what sits under it (a party's page → Parties; Record expense →
 *  Money movements in the `daily` set), New entry for every door in the `astra` set, More for everything else. */
export function phoneTabOwner(pathname: string): string {
  if (pathname === '/') return '/';
  const r = rowFor(pathname);
  const has = (p: string) => PHONE_TABS.includes(p);
  if (r && has(r.path)) return r.path;
  if (pathname.startsWith('/parties') && has('/parties')) return '/parties';
  // A cheque's progress (/mc/<id>) is a trade being worked, not the door that books one: Trades where it is a tab, else More.
  if (pathname.startsWith('/mc/')) return has('/trades') ? '/trades' : '/more';
  if (r && (r.path === '/new' || r.v2?.entry || r.path === '/treasury/adjust') && has('/new')) return '/new';
  if (r?.v2?.under && has(r.v2.under)) return r.v2.under;
  return '/more';
}

/** The top bar's breadcrumb: the group the open screen belongs to. */
export function screenGroup(pathname: string): string {
  const r = rowFor(pathname);
  if (!r || pathname.startsWith('/mc/')) return 'Daily work';
  if (r.v2?.entry || r.path === '/new' || r.path === '/treasury/adjust') return 'New entry';
  if (r.v2?.rail === 'daily' || pathname.startsWith('/parties')) return 'Daily work';
  return r.v2?.section ?? 'More';
}

